import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { Subscription } from 'rxjs';
import { ProfileCard, ProfileDataSection } from '../../interfaces/generic.interface';
import { AuthStateService } from '../../services/authstate.service';
import { NotificationsService } from '../../services/notifications.service';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';

@Component({
  selector: 'app-profile-view',
  imports: [
    RippleModule,
    FormsModule,
    FloatLabelModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    TranslocoDirective,
    ButtonModule,
    CommonModule,
    PasswordModule,
    ToggleButtonModule,
    CheckboxModule,
    TranslocoDatePipe
  ],
  templateUrl: './profile-view.component.html',
  styleUrl: './profile-view.component.scss'
})
export class ProfileViewComponent implements AfterViewInit {

  @ViewChildren(TemplateRef) templates!: QueryList<TemplateRef<any>>;

  activeSection: string = "personalInfo";

  mediumRegex = '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})';
  strongRegex = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&#])(?=.{8,})';

  updateSub?: Subscription;


  dataSections: ProfileDataSection[] = [
    {
      id: "personalInfo",
      title: "personalInfo",
      icon: "person",
      cards: [
        {
          title: "baseInfo",
          originalData: structuredClone(this.authState.loginData.user),
          data: structuredClone(this.authState.loginData.user),
          isEditing: false,
          templateName: "baseProfileInfo",
          updateEndpoint: "updateUserPublicBase",
          afterUpdate: (newData) => {
            this.authState.loginData.user = { ...this.authState.loginData.user, ...newData };
          }
        },
        {
          title: "emailAddress",
          subtitle: "emailAddressChangeWarning",
          originalData: { email: this.authState.loginData.user.email },
          data: { email: this.authState.loginData.user.email },
          isEditing: false,
          templateName: "profileEmail",
          updateEndpoint: "updateUserPublicEmail",
          afterUpdate: (newData, res) => {
            if (res.code == 200) {
              window.location.reload()
            }
          }
        },
        {
          title: "password",
          subtitle: "passwordProfileDescription",
          originalData: { password: "" },
          data: { password: "" },
          isEditing: false,
          templateName: "profilePassword",
          updateEndpoint: "updateUserPublicPassword",
          afterUpdate: () => {
            this.authState.logout(true, this.router.url);
          },
          validatorFn: (data) => {
            return data.password == data.passwordConfirm && data.password.match(this.strongRegex);
          }
        }
      ]
    },
    {
      id: "dataAndPrivacy",
      title: "dataAndPrivacy",
      icon: "security",
      cards: [
        {
          title: "consentsManagement",
          originalData: [],
          data: [],
          isEditing: false,
          templateName: "profileConsents",
          updateEndpoint: "updateUserPublicConsents",
          getEndpoint: "getUserConsents",
          updateTransform: (data) => {
            let json = data.reduce((acc: any, curr: any) => {
              acc[curr.consentType] = curr.consentAccepted;
              return acc;
            }, {});

            return { consents: json }
          }
        },
        {
          title: "downloadPersonalData",
          subtitle: "downloadPersonalDataDescription",
          originalData: {},
          data: {},
          isEditing: false,
          templateName: "profileDataDownload",
          updateEndpoint: "downloadPersonalData",
          showFormButtons: false,
          afterUpdateOverride: true,
        },
        {
          title: "deleteAccount",
          subtitle: "accountDeletionWarning",
          originalData: {},
          data: {},
          isEditing: false,
          templateName: "profileDelete",
          updateEndpoint: "requestAccountDeletion",
          showFormButtons: false,
          afterUpdateOverride: true,
        },
      ]
    },
    {
      id: "payments",
      title: "paymentsAndBilling",
      icon: "credit_card",
      cards: [
      ]
    }
  ]

  get currentSectionCards() {
    return this.dataSections.find(section => section.id === this.activeSection)!.cards;
  }

  constructor(
    private authState: AuthStateService,
    private http: HttpClient,
    private notifications: NotificationsService,
    private router: Router,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
  }

  ngAfterViewInit(): void {
    const templateElements = this.templates.toArray();

    setTimeout(() => {

      this.dataSections.forEach(section => {
        section.cards.forEach(card => {
          // Find template by name from the DOM
          const template = templateElements.find(t =>
            (t as any)['_declarationTContainer']?.localNames?.[0] === card.templateName
          );
          if (template) {
            card.template = template;
          }
          this.refreshCardData(card)
        });
      });
    }, 1);
  }

  cardEnableEdit(card: ProfileCard) {
    card.isEditing = true;
    card.data = structuredClone(card.originalData);
  }

  cardCancelEdits(card: ProfileCard) {
    card.isEditing = false;
    card.data = structuredClone(card.originalData);
    if (this.updateSub) {
      this.updateSub.unsubscribe();
      this.updateSub = undefined;
      card.isLoading = false;
    }
  }

  cardSaveEdits(card: ProfileCard) {
    if (this.updateSub) {
      this.updateSub.unsubscribe();
      this.updateSub = undefined;
      card.isLoading = false;
    }
    card.isLoading = true;
    this.updateSub = this.http.post(this.ENV.apiUrl + card.updateEndpoint, card.updateTransform ? card.updateTransform(card.data) : card.data).subscribe({
      next: (res: any) => {
        card.isLoading = false;
        if (!(card.afterUpdateOverride ?? false)) {

          card.isEditing = false;
          card.originalData = structuredClone(card.data);
          if (res.code == 200) {
            this.notifications.showNotification({
              severity: "success",
              title: "operationComplete",
              detail: "dataUpdatedCorrectly",
              addToHistory: false
            })
          } else if (res.code == 201) {
            this.notifications.showNotification({
              severity: "info",
              title: "noModifications",
              addToHistory: false
            })
          }
        }
        if (card.afterUpdate) {
          card.afterUpdate(card.data, res);
        }

        this.refreshCardData(card);
      },
      error: (err: any) => {
        card.isLoading = false;
      }
    });

  }

  loadForm(card: ProfileCard, form: NgForm) {
    card.form = form;
    return '';
  }

  refreshCardData(card: ProfileCard) {
    if (card.getEndpoint) {
      card.isLoading = true;
      this.http.post(this.ENV.apiUrl + card.getEndpoint, card.getParameters).subscribe({
        next: (res: any) => {
          card.isLoading = false;
          card.data = structuredClone(res.data);
          card.originalData = structuredClone(res.data);
        },
        error: (err) => {
          card.isLoading = false;
        }
      });
    }
  }
}
